/**
 * offlineCart.js — local cart-mutation queue persisted in IndexedDB.
 *
 * The React app pushes a `{ kind, payload }` operation onto the queue whenever
 * the user is offline. When the browser comes back online (or on app boot)
 * `flushOfflineQueue()` replays the queue against the live `/api/cart/*` API.
 *
 * Operations are intentionally simple — the cart endpoints are idempotent
 * enough that replaying duplicates has no destructive effect.
 */
import { get, set, del } from "idb-keyval";
import { toast } from "sonner";
import { api } from "./api";

const KEY = "kb_offline_cart_queue";

async function read() {
  return (await get(KEY)) || [];
}
async function write(items) {
  if (!items.length) return del(KEY);
  return set(KEY, items);
}

export async function enqueue(op) {
  const items = await read();
  items.push({ ...op, ts: Date.now() });
  await write(items);
}

export async function pendingCount() {
  return (await read()).length;
}

export async function clearOfflineQueue() {
  await del(KEY);
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

let flushing = false;

/**
 * Drain the queue against the API. Best-effort: if any single op throws a
 * network error, we re-enqueue the remaining ops and bail. Returns the count
 * of successfully processed ops.
 */
export async function flushOfflineQueue() {
  if (flushing) return 0;
  flushing = true;
  try {
    let queue = await read();
    if (!queue.length) return 0;
    let synced = 0;
    let dropped = 0;
    let authFailure = false;
    while (queue.length) {
      const op = queue[0];
      try {
        await applyOp(op);
        queue = queue.slice(1);
        await write(queue);
        synced += 1;
      } catch (err) {
        // Network down again — leave the rest queued for next attempt
        if (!isOnline() || err?.code === "ERR_NETWORK" || !err?.response) {
          break;
        }
        // 401 — auth dropped (token expired / user logged out). Stop draining
        // so the user has a chance to re-auth without losing the queue.
        if (err.response.status === 401) {
          authFailure = true;
          break;
        }
        // Server returned 4xx (e.g. product deleted) — drop the op and continue
        queue = queue.slice(1);
        await write(queue);
        dropped += 1;
      }
    }
    if (dropped > 0) {
      toast.message(`${dropped} pending change${dropped > 1 ? "s" : ""} couldn't be applied`);
    }
    if (authFailure) {
      toast.error("Sign in to sync your pending cart changes");
    }
    return synced;
  } finally {
    flushing = false;
  }
}

async function applyOp(op) {
  switch (op.kind) {
    case "add":
      return api.post("/cart/items", op.payload);
    case "update":
      return api.put(`/cart/items/${op.payload.product_id}`,
        { qty: op.payload.qty },
        { params: { size: op.payload.size || "" } });
    case "remove":
      return api.delete(`/cart/items/${op.payload.product_id}`,
        { params: { size: op.payload.size || "" } });
    case "wishlist_toggle":
      return api.post(`/wishlist/toggle/${op.payload.product_id}`);
    default:
      // Unknown op — discard
      return null;
  }
}
